import React from 'react'
import { string, array, object, number, bool } from 'prop-types'
import styles from './EventContentGrid.module.scss'
import Grid from '@/component/Primitive/Grid'
import Card from './components/Card'
import VideoCard from './components/VideoCard'
import SponsorCard from './components/SponsorCard'
import SpeakerCard from './components/SpeakerCard'
import AgendaItemCard from './components/AgendaItemCard'
import EventCard from './components/EventCard'
import EventUmbrellaCard from './components/EventUmbrellaCard'
import cx from 'classnames'
import moment from 'moment'
import Container from '@/component/Primitive/Container'
import Stack from '@/component/Primitive/Stack'
import { Highlight, ButtonGroup } from '@/component/Primitive/Events'
import Type from '@/component/Primitive/Type'

export const EventContentGrid = ({
  list,
  theme,
  title,
  subtitle,
  buttonGroup,
  baseUrl,
  limit,
  openInNewTab
}) => {
  const dayMap = {}
  const inverseDayMap = {}
  let hideStageLabel = false

  const align = list[0] ? list[0]?.align : 'left'
  const contentType = list[0] ? list[0]?.type : 'video'

  if (contentType === 'agendaItem') {
    let dayCount = 1
    let lastAgendaDate = null
    for (const item of list) {
      if (!lastAgendaDate) {
        dayMap[dayCount] = [{ ...item }]
        inverseDayMap[item._id] = dayCount
        lastAgendaDate = item.startDate
      } else {
        const isSameDay = moment(item.startDate).isSame(lastAgendaDate, 'day')
        if (isSameDay) {
          dayMap[dayCount].push({ ...item })
          inverseDayMap[item._id] = dayCount
          lastAgendaDate = item.startDate
        } else {
          dayCount++
          dayMap[dayCount] = [{ ...item }]
          inverseDayMap[item._id] = dayCount
          lastAgendaDate = item.startDate
        }
      }
    }
    hideStageLabel = Object.keys(dayMap).length === 1
  }

  const mapContentPropsToProps = (content, i) => {
    switch (content.type) {
      case 'video':
        return {
          ...content,
          tag: 'On Demand Video',
          key: content.video.name,
          title:
            content.title || content.video.name.length < 60
              ? content.video.name
              : content.video.name.slice(0, 60) + '...',
          subtitle: content.subtitle || content.video.eventName,
          description: content.video.description || 'description...',
          ctaLabel: content.ctaLabel || 'Watch now',
          theme: content.theme || theme,
          align: align || 'left',
          numItemsInRow: { m: 2, t: 2, d: 4 },
          sessionType: content.video.agendaItemSessionType,
          previewId: content.video.previewId,
          openInNewTab
        }
      case 'article':
        return {
          ...content,
          tag: 'Event Article',
          key: content.article.headline,
          title: content.title || content.article.headline,
          subtitle: content.subtitle || content.article.category || 'subtitle',
          description:
            content.description || content.article.sell || '...description',
          ctaLabel: content.ctaLabel || 'Read full article',
          theme: content.theme || theme,
          openInNewTab
        }
      case 'sponsor':
        return {
          ...content,
          tag: 'Event Sponsor',
          key: content.sponsor.displayName,
          title: content.title || content.sponsor.displayName,
          subtitle: content.sponsor.tier.name
            ? content.sponsor.tier.name.charAt(0).toUpperCase() +
              content.sponsor.tier.name.slice(1)
            : 'Partner',
          description: content.description,
          ctaLabel: content.ctaLabel || 'Visit sponsor booth',
          logo: content.logo,
          theme: content.theme || theme,
          align: align || 'center',
          numItemsInRow: { m: 2, t: 4, d: 6 },
          openInNewTab
        }
      case 'speaker':
        return {
          ...content,
          tag: 'Event Speaker',
          key: content.speaker.name,
          title: content.title || content.speaker.name,
          subtitle: content.subtitle || content.speaker.jobTitle,
          companyName: content.speaker.companyName,
          roles: content.speaker.roles,
          nextAgendaItem: content.speaker.nextAgendaItem,
          description: content.description,
          ctaLabel: content.ctaLabel || 'Read speaker bio',
          theme: content.theme || theme,
          align: align || 'center',
          numItemsInRow: { m: 2, t: 3, d: 4 },
          openInNewTab
        }
      case 'agendaItem': {
        const previousStartDate = list[i - 1]?.startDate
        const currentStartDate = content.startDate

        const showDatePrefix =
          !moment(previousStartDate).isSame(currentStartDate, 'day') ||
          !previousStartDate

        return {
          ...content,
          tag: 'Event Agenda',
          key: content.agendaItem.name,
          title: content.title || content.agendaItem.name,
          subtitle: content.subtitle || content.agendaItem.sessionType.name,
          description:
            content.description ||
            content.agendaItem.description ||
            '...description',
          speakers: content.agendaItem.speakers,
          sessionType: content.agendaItem.sessionType.shortName,
          time: `${content.localeSafeStartTime} - ${content.localeSafeEndTime} (${content.timezoneAbbreviation})`,
          startDate: content.agendaItem.startDate,
          endDate: content.agendaItem.endDate,
          ctaLabel: content.ctaLabel || 'Read about topic',
          theme: content.theme || theme,
          align: align || 'center',
          numItemsInRow: { m: 1, t: 1, d: 1 },
          shaded: i % 2 === 0,
          showDatePrefix,
          day: Object.keys(dayMap).length > 1 && inverseDayMap[content._id],
          baseUrl,
          location: content.agendaItem.location,
          hideStageLabel: limit ? false : hideStageLabel,
          openInNewTab
        }
      }
      case 'event': {
        const startDate = moment(content.event.startDate)
        const endDate = moment(content.event.endDate)
        let dateRange
        if (startDate.isSame(content.event.endDate, 'day')) {
          dateRange = `${startDate.format('MMM DD YYYY')}`
        } else {
          dateRange = `${startDate.format('MMM DD')}-${endDate.format(
            'Do YYYY'
          )}`
        }

        return {
          ...content,
          tag: 'Event',
          key: content.event.name,
          title: content.title || content.event.name,
          subtitle:
            content.subtitle || dateRange + ', ' + content.event.buildingName,
          description: content.description || content.event.description,
          ctaLabel: content.ctaLabel,
          theme: content.theme || theme,
          numItemsInRow: { m: 2, t: 3, d: 4 },
          align: align || 'left',
          openInNewTab
        }
      }
      case 'eventUmbrella':
        return {
          ...content,
          tag: 'Event Umbrella',
          key: content.eventUmbrella.name,
          title: content.title || content.eventUmbrella.name,
          subtitle: content.subtitle,
          description: content.description || content.eventUmbrella.description,
          ctaLabel: content.ctaLabel,
          theme: content.theme || theme,
          align: 'center',
          openInNewTab
        }
      default:
        break
    }
    return null
  }

  const ComponentMap = {
    video: VideoCard,
    article: Card,
    sponsor: SponsorCard,
    speaker: SpeakerCard,
    agendaItem: AgendaItemCard,
    event: EventCard,
    eventUmbrella: EventUmbrellaCard
  }

  const Wrapper = contentType === 'agendaItem' ? 'div' : Grid
  const Inner = contentType === 'agendaItem' ? 'div' : Grid.Item

  return (
    <div
      className={cx(
        styles.EventContentGrid,
        theme === 'grey' && styles.grey,
        theme === 'dark' && styles.dark,
        align === 'center' && styles.center,
        contentType === 'video' && styles.video
      )}
    >
      <svg
        width="347"
        height="320"
        viewBox="0 0 347 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.Shape1}
      >
        <mask id="path-1-inside-1_10047_8715" fill="white">
          <path d="M344.754 155.651L282.547 291.033C278.867 299.044 271.177 304.506 262.376 305.365L113.625 319.882C104.824 320.741 96.2457 316.864 91.1246 309.711L4.57483 188.852C-0.546225 181.7 -1.43417 172.36 2.24607 164.349L64.4583 28.9673C68.1385 20.9558 75.8285 15.4939 84.6298 14.6347L233.381 0.117998C242.182 -0.741177 250.76 3.13627 255.881 10.2886L342.425 131.153C347.546 138.306 348.434 147.645 344.754 155.657" />
        </mask>
        <path
          d="M282.547 291.033L281.639 290.615L281.639 290.615L282.547 291.033ZM262.376 305.365L262.473 306.361L262.473 306.361L262.376 305.365ZM113.625 319.882L113.528 318.887L113.528 318.887L113.625 319.882ZM91.1246 309.711L91.9377 309.129L91.9377 309.129L91.1246 309.711ZM4.57483 188.852L3.76176 189.434L3.76181 189.434L4.57483 188.852ZM2.24607 164.349L1.33741 163.931L1.33736 163.931L2.24607 164.349ZM64.4583 28.9673L65.3669 29.3849L65.367 29.3848L64.4583 28.9673ZM84.6298 14.6347L84.5327 13.6394L84.5326 13.6395L84.6298 14.6347ZM233.381 0.117998L233.478 1.11327L233.478 1.11327L233.381 0.117998ZM255.881 10.2886L255.068 10.8708L255.068 10.8708L255.881 10.2886ZM342.425 131.153L343.238 130.571L343.238 130.571L342.425 131.153ZM343.845 155.234L281.639 290.615L283.456 291.45L345.663 156.069L343.845 155.234ZM281.639 290.615C278.107 298.302 270.727 303.545 262.279 304.37L262.473 306.361C271.627 305.467 279.627 299.786 283.456 291.45L281.639 290.615ZM262.279 304.37L113.528 318.887L113.722 320.877L262.473 306.361L262.279 304.37ZM113.528 318.887C105.08 319.711 96.8495 315.989 91.9377 309.129L90.3116 310.294C95.6419 317.738 104.568 321.771 113.722 320.877L113.528 318.887ZM91.9377 309.129L5.38786 188.27L3.76181 189.434L90.3116 310.294L91.9377 309.129ZM5.38791 188.27C0.476389 181.41 -0.376012 172.452 3.15477 164.766L1.33736 163.931C-2.49233 172.268 -1.56884 181.989 3.76176 189.434L5.38791 188.27ZM3.15472 164.766L65.3669 29.3849L63.5496 28.5498L1.33741 163.931L3.15472 164.766ZM65.367 29.3848C68.8981 21.6979 76.2781 16.4548 84.7269 15.63L84.5326 13.6395C75.3789 14.533 67.379 20.2137 63.5496 28.5499L65.367 29.3848ZM84.7269 15.63L233.478 1.11327L233.284 -0.877273L84.5327 13.6394L84.7269 15.63ZM233.478 1.11327C241.926 0.288559 250.156 4.01067 255.068 10.8708L256.694 9.70647C251.364 2.26187 242.438 -1.77091 233.283 -0.877271L233.478 1.11327ZM255.068 10.8708L341.612 131.736L343.238 130.571L256.694 9.70645L255.068 10.8708ZM341.612 131.736C346.524 138.595 347.376 147.553 343.845 155.239L345.663 156.074C349.492 147.737 348.569 138.016 343.238 130.571L341.612 131.736Z"
          fill="white"
          mask="url(#path-1-inside-1_10047_8715)"
        />
      </svg>

      <svg
        width="347"
        height="320"
        viewBox="0 0 347 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.Shape2}
      >
        <path
          d="M344.754 155.651L282.547 291.033C278.867 299.044 271.177 304.506 262.376 305.365L113.625 319.882C104.824 320.741 96.2457 316.864 91.1246 309.711L4.57483 188.852C-0.546225 181.7 -1.43417 172.36 2.24607 164.349L64.4583 28.9673C68.1385 20.9558 75.8285 15.4939 84.6298 14.6347L233.381 0.117998C242.182 -0.741177 250.76 3.13627 255.881 10.2886L342.425 131.153C347.546 138.306 348.434 147.645 344.754 155.657"
          fill="#FF620F"
        />
      </svg>

      <Container
        center
        size={contentType === 'speaker' ? 'large' : 'wide'}
        gutter
        className={styles.EventDetailsContainer}
      >
        <Stack gap="medium" className={styles.Stack}>
          <div gap="small" className={styles.StackInner}>
            <div className={styles.TitleWrapper}>
              {title && (
                <Type
                  as="h2"
                  className={styles.Title}
                  size={['heading5', 'heading2']}
                  weight="bold"
                >
                  <Highlight
                    classNames={{
                      strong: styles.Highlight
                    }}
                  >
                    {title}
                  </Highlight>
                </Type>
              )}
              {subtitle && (
                <Type as="p" className={styles.Subtitle} size="body4">
                  {subtitle}
                </Type>
              )}
            </div>
            {!!buttonGroup?.length && contentType === 'video' && (
              <ButtonGroup
                group={buttonGroup}
                className={cx(
                  styles.ButtonGroup,
                  contentType === 'video' && styles.videoDesktopOnly
                )}
                center={align === 'center'}
                theme={theme}
              />
            )}
          </div>
          <Wrapper size="small" flex gutter="default" className={styles.Grid}>
            {list?.map((content, i) => {
              const Component = ComponentMap[content.type]
              const embellishedProps = mapContentPropsToProps(content, i)
              return (
                <Inner
                  key={`${embellishedProps.key}-${i}`}
                  width={{
                    m: 1 / (embellishedProps?.numItemsInRow?.m || 2),
                    t: 1 / (embellishedProps?.numItemsInRow?.t || 4),
                    d: 1 / (embellishedProps?.numItemsInRow?.d || 4)
                  }}
                  trailingGap="default"
                >
                  <Component theme={theme} {...embellishedProps} />
                </Inner>
              )
            })}
          </Wrapper>
          {!!buttonGroup?.length && (
            <ButtonGroup
              group={buttonGroup}
              className={cx(
                styles.ButtonGroup,
                contentType === 'video' && styles.videoMobileOnly
              )}
              center={align === 'center'}
              theme={theme}
            />
          )}
        </Stack>
      </Container>
    </div>
  )
}

EventContentGrid.propTypes = {
  openInNewTab: bool,
  list: array,
  theme: string,
  image: object,
  title: string,
  subtitle: string,
  buttonGroup: array,
  baseUrl: string,
  limit: number
}
